import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { type EnvName } from "./api";

type VoiceSessionValue = {
  env: EnvName;
  sessionId: string;
  newShift: () => void;
};

const VoiceSessionContext = createContext<VoiceSessionValue | null>(null);

function mintSessionId(): string {
  return crypto.randomUUID();
}

function envFromPath(pathname: string): EnvName {
  return pathname.startsWith("/sim") ? "sim" : "live";
}

function sessionStorageKey(env: EnvName): string {
  return `og-orm-voice-session-id-${env}`;
}

function loadOrMintSessionId(env: EnvName): string {
  const key = sessionStorageKey(env);
  try {
    const saved = sessionStorage.getItem(key);
    if (saved) return saved;
  } catch {
    // sessionStorage unavailable (e.g. private mode with strict settings)
  }
  const id = mintSessionId();
  try {
    sessionStorage.setItem(key, id);
  } catch {
    // ignore write failure
  }
  return id;
}

export function VoiceSessionProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const env = envFromPath(pathname);
  const [sessionId, setSessionId] = useState<string>(() => loadOrMintSessionId(env));

  useEffect(() => {
    setSessionId(loadOrMintSessionId(env));
  }, [env]);

  const newShift = useCallback(() => {
    const id = mintSessionId();
    try {
      sessionStorage.setItem(sessionStorageKey(env), id);
    } catch {
      // ignore
    }
    setSessionId(id);
  }, [env]);

  const value = useMemo(
    () => ({ env, sessionId, newShift }),
    [env, sessionId, newShift],
  );

  return (
    <VoiceSessionContext.Provider value={value}>
      {children}
    </VoiceSessionContext.Provider>
  );
}

export function useVoiceSession(): VoiceSessionValue {
  const ctx = useContext(VoiceSessionContext);
  if (!ctx) {
    throw new Error("useVoiceSession must be used within VoiceSessionProvider");
  }
  return ctx;
}
